import React, { useEffect, useState } from 'react';
import { db } from '../../firebaseConfig';
import {
  collection,
  doc,
  getDoc,
  query,
  where,
  addDoc,
  serverTimestamp,
  getDocs,
} from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import './PublicAssetPage.css';
import { useTranslation } from 'react-i18next';

const emojiOptions = ['😊', '😐', '😞', '🔥', '👍'];

const PublicAssetPage = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const [asset, setAsset] = useState(null);
  const [sentiment, setSentiment] = useState('');
  const [comment, setComment] = useState('');
  const [pin, setPin] = useState('');
  const [issueDescription, setIssueDescription] = useState('');
  const [issues, setIssues] = useState([]);

  useEffect(() => {
    const fetchAsset = async () => {
      const q = query(collection(db, 'assets'), where('token', '==', token));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const assetData = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
        setAsset(assetData);
        fetchIssues(assetData.id);
      }
    };
    fetchAsset();
  }, [token]);

  const fetchIssues = async (assetId) => {
    const q = query(collection(db, 'asset_issues'), where('asset_id', '==', assetId));
    const snapshot = await getDocs(q);
    const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setIssues(results);
  };

  const handleSubmitFeedback = async () => {
    if (!sentiment && !comment) return alert(t('publicAsset.feedbackRequired'));
    try {
      await addDoc(collection(db, 'asset_reviews'), {
        asset_id: asset.id,
        sentiment,
        comment,
        createdAt: serverTimestamp(),
      });
      alert(t('publicAsset.feedbackSubmitted'));
      setSentiment('');
      setComment('');
    } catch (err) {
      console.error(err);
      alert(t('publicAsset.feedbackError'));
    }
  };

  const handleSubmitIssue = async () => {
    if (!pin || !issueDescription) return alert(t('publicAsset.fillAllFields'));
    if (pin !== asset.admin_pin) return alert(t('publicAsset.incorrectPin'));

    try {
      await addDoc(collection(db, 'asset_issues'), {
        asset_id: asset.id,
        asset_token: asset.token,
        description: issueDescription,
        createdAt: serverTimestamp(),
        resolved: false,
        admin_responses: [],
      });
      alert(t('publicAsset.issueReported'));
      setIssueDescription('');
      setPin('');
      fetchIssues(asset.id);
    } catch (err) {
      console.error(err);
      alert(t('publicAsset.issueError'));
    }
  };

  if (!asset) return <div className="text-center mt-10">{t('publicAsset.loading')}</div>;

  return (
    <div className="asset-page-container">
      <div className="asset-info">
        <h2>🛠️ {asset.model}</h2>
        <p><strong>{t('publicAsset.serialNumber')}:</strong> {asset.serial_number}</p>
        <p><strong>{t('publicAsset.category')}:</strong> {asset.category}</p>
        <p><strong>{t('publicAsset.location')}:</strong> {asset.location}</p>
        {asset.guide_use && (
          <div className="asset-text-block">
            <strong>{t('publicAsset.guideUse')}:</strong>
            <p className="asset-text-content">{asset.guide_use}</p>
          </div>
        )}

        {asset.safety_conditions && (
          <div className="asset-text-block">
            <strong>{t('publicAsset.safetyConditions')}:</strong>
            <p className="asset-text-content">{asset.safety_conditions}</p>
          </div>
        )}
      </div>

      <div>
        <p className="section-title">💬 {t('publicAsset.sentimentFeedback')}</p>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
          {emojiOptions.map((e) => (
            <button
              key={e}
              onClick={() => setSentiment(e)}
              style={{
                fontSize: '24px',
                background: sentiment === e ? '#d4d4d4' : '#f2f2f2',
                borderRadius: '6px',
                padding: '6px 12px',
              }}
            >
              {e}
            </button>
          ))}
        </div>
        <textarea
          rows="3"
          placeholder={t('publicAsset.commentPlaceholder')}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <button onClick={handleSubmitFeedback}>{t('publicAsset.submitFeedback')}</button>
      </div>

      <div>
        <p className="section-title">🐞 {t('publicAsset.reportIssue')}</p>
        <textarea
          rows="3"
          placeholder={t('publicAsset.issuePlaceholder')}
          value={issueDescription}
          onChange={(e) => setIssueDescription(e.target.value)}
        />
        <input
          type="password"
          placeholder={t('publicAsset.pinPlaceholder')}
          value={pin}
          onChange={(e) => setPin(e.target.value)}
        />
        <button onClick={handleSubmitIssue}>{t('publicAsset.submitIssue')}</button>
      </div>

      <div>
        <p className="section-title">📋 {t('publicAsset.reportedIssues')}</p>
        {issues.length === 0 && <p>{t('publicAsset.noIssues')}</p>}
        {issues.map((issue, index) => (
          <div key={index} className="issue-card">
            <strong>{issue.description}</strong>
            <small>{issue.createdAt?.toDate().toLocaleString()}</small>
            {issue.admin_responses?.length > 0 && (
              <div style={{ marginTop: '6px', fontStyle: 'italic', color: '#444' }}>
                <strong>{t('publicAsset.adminReplies')}:</strong>
                <ul className="response-list">
                  {issue.admin_responses.map((resp, idx) => (
                    <li key={idx}>
                      🧠 {resp.text} — <span className="text-xs">{resp.timestamp?.toDate().toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PublicAssetPage;
